import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '@app/core/services/loader/loader.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { InksoftService } from '@app/shared/services/inksoft/inksoft.service';
import { ErrorhandlerService } from '@app/shared/services/errorhandler/errorhandler.service';
import { StateService } from '@app/shared/services/state/state.service';
import { CommonService } from '@app/shared/services/common/common.service';
import { UploadDesignService } from '@app/feature/store-admin/services/uploadDesign/uploadDesign.service';
import { PdfThumbnailService } from '@app/shared/services/pdfThumbnail/pdf-thumbnail.service';
import { ImageTrimService } from '@app/shared/services/imageTrim/image-trim.service';
import { Location } from '@angular/common';
import { environment } from '@env/environment';
import { getRoutePrefix } from '@app/shared/utils/route-prefix.util';

interface SavedDesign {
  id: number;
  name: string;
  thumbnailUrl: string;
  createdDate: Date;
  designId: number;
}

const PAGE_SIZE = 30;

// Rasterization width for PDF design thumbnails, in CSS pixels. The card is ~300px wide, so this
// stays sharp on a 2x display without paying for pixels the grid never shows.
const PDF_THUMBNAIL_WIDTH = 600;

// Kept just under StoreBuilderConstants.MaxArtworkUploadBase64Length (~34.9 MB), the server's cap
// on the base64 string. Leaving headroom means a converted PDF is re-rendered smaller here rather
// than rejected by the API.
const MAX_UPLOAD_BASE64_LENGTH = 34_000_000;

@Component({
  selector: 'app-upload-design',
  templateUrl: './upload-design.component.html',
  styleUrls: ['./upload-design.component.css']
})
export class UploadDesignComponent implements OnInit, AfterViewInit, OnDestroy {
  storeId: number;
  storeName: string;
  superAdminLoggedIn: boolean = false;
  userRole: string;

  // Tab management
  activeTab: 'upload' | 'saved' = 'upload';

  // Upload section
  selectedFile: File | null = null;
  isDragging: boolean = false;
  isUploading: boolean = false;
  // True while a PDF is being rasterized to PNG ahead of the upload. Rendering a large page can
  // take a moment, and without this the UI sits at 0% with no explanation.
  isConvertingPdf: boolean = false;
  uploadProgress: number = 0;
  uploadComplete: boolean = false;
  filePreviewUrl: string | null = null;
  uploadedLogoUrl: string | null = null;

  // Saved designs section — IntersectionObserver pagination
  savedDesigns: SavedDesign[] = [];
  savedDesignsTotal: number = 0;
  isLoadingDesigns: boolean = false;
  isLoadingMore: boolean = false;
  allDesignsLoaded: boolean = false;
  private currentSkip: number = 0;
  searchText: string = '';

  // IntersectionObserver sentinel
  private _sentinelEl: ElementRef | null = null;
  private _observer: IntersectionObserver | null = null;

  @ViewChild('scrollSentinel')
  set scrollSentinel(el: ElementRef) {
    // Called when *ngIf brings the sentinel into the DOM (or removes it).
    if (el && el !== this._sentinelEl) {
      this._sentinelEl = el;
      this._attachObserver(el.nativeElement);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loader: LoaderService,
    private notification: NotificationService,
    private inksoftService: InksoftService,
    private errorHandler: ErrorhandlerService,
    private stateService: StateService,
    private commonService: CommonService,
    private uploadDesignService: UploadDesignService,
    private location: Location,
    private pdfThumbnail: PdfThumbnailService,
    private imageTrim: ImageTrimService
  ) {
    this.route.queryParams.subscribe(params => {
      this.storeId = params['storeId'] || localStorage.getItem('storeId');
      this.storeName = params['storeName'] || localStorage.getItem('storeName') || '';
    });
  }

  ngOnInit(): void {
    this.userRole = localStorage.getItem('role');
    if (this.userRole === 'SuperAdmin' || this.userRole === 'CustomerService') {
      this.superAdminLoggedIn = true;
    }

    // Fire first page on init — populates badge count from TotalDataCount.
    // No separate takeRecords=0 probe needed.
    this.fetchNextPage();
  }

  ngAfterViewInit(): void {
    // Sentinel may already be in the DOM if activeTab starts as 'saved'.
    // For the default 'upload' tab the @ViewChild setter handles it when the
    // user switches tabs and the *ngIf renders the sentinel.
  }

  ngOnDestroy(): void {
    this._disconnectObserver();
  }

  private _attachObserver(el: Element): void {
    this._disconnectObserver();
    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.onScroll();
        }
      },
      { threshold: 0.1 }
    );
    this._observer.observe(el);
  }

  private _disconnectObserver(): void {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  // Navigation
  goBack(): void {
    const prefix = getRoutePrefix(this.router.url);
    if (prefix === '/store') {
      this.router.navigate(['/store/publishedproducts']);
    } else {
      this.router.navigate(['/superadmin/shopify-products', this.storeId, this.storeName]);
    }
  }

  // File upload handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  handleFile(file: File): void {
    // Validate file type — PNG, JPG, PDF and SVG are supported. This is a UX gate only; the
    // server-side allow-list in UploadHuddleLogoHandler is the real control (it also sniffs the
    // bytes and sanitizes SVG, which is XML and can carry script).
    const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf', 'image/svg+xml'];
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.pdf', '.svg'];

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    // Deliberately an OR: some browsers report an empty file.type for .svg, so the extension
    // check is the reliable half.
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      this.notification.error('File type not supported. Please upload PNG, JPG, PDF, or SVG files.', 'Error');
      return;
    }

    // Validate file size (25 MB = 25 * 1024 * 1024 bytes)
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      this.notification.error('File size exceeds 25 MB limit.', 'Error');
      return;
    }

    this.selectedFile = file;
    this.uploadProgress = 0;
    this.uploadComplete = false;

    // Generate preview URL for image files
    this.generateFilePreview(file);

    // Auto-trigger upload
    this.uploadDesign();
  }

  generateFilePreview(file: File): void {
    const imageTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (imageTypes.includes(file.type)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreviewUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    } else {
      this.filePreviewUrl = null;
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    this.uploadProgress = 0;
    this.uploadComplete = false;
    this.filePreviewUrl = null;
    this.isUploading = false;
    this.uploadedLogoUrl = null;
  }

  getFileIcon(): string {
    if (!this.selectedFile) return 'fa-file';

    const extension = this.selectedFile.name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'svg':
        return 'fa-file-code-o';
      case 'png':
      case 'jpg':
      case 'jpeg':
        return 'fa-file-image-o';
      case 'pdf':
        return 'fa-file-pdf-o';
      case 'eps':
        return 'fa-file-image-o';
      default:
        return 'fa-file';
    }
  }

  // The image URL to show for a design card.
  //
  // A browser cannot decode a PDF inside an <img>, so PDF designs are rasterized to a PNG data URL
  // first and that is what gets bound. Returns null while the render is in flight (the card shows
  // a spinner) and if the render failed (the card falls back to the file-type tile).
  designThumbnailSrc(design: SavedDesign): string | null {
    const url = design.thumbnailUrl;
    if (!this.pdfThumbnail.isPdfUrl(url)) {
      return url || 'assets/img/default-design.png';
    }

    // Undefined (still rendering) and null (render failed) both mean "no image to bind"; the
    // template distinguishes them via isPdfRendering / isPdfRenderFailed. Reads only — the render
    // itself is started in fetchNextPage so nothing is triggered from change detection.
    return this.pdfThumbnail.peek(url) ?? null;
  }

  // True once a PDF card's render has finished and failed, so the tile should replace the spinner.
  isPdfRenderFailed(design: SavedDesign): boolean {
    return this.pdfThumbnail.isPdfUrl(design.thumbnailUrl)
      && this.pdfThumbnail.peek(design.thumbnailUrl) === null;
  }

  // True while a PDF card is still rasterizing.
  isPdfRendering(design: SavedDesign): boolean {
    return this.pdfThumbnail.isPdfUrl(design.thumbnailUrl)
      && this.pdfThumbnail.peek(design.thumbnailUrl) === undefined;
  }

  // Any unreachable thumbnail (deleted blob, bad URL) falls back to the placeholder
  // rather than leaving a broken-image box in the grid.
  onThumbnailError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const fallback = 'assets/img/default-design.png';
    // Guard against an error loop if the fallback itself is missing.
    if (!img.src.endsWith(fallback)) {
      img.src = fallback;
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  uploadDesign(): void {
    if (!this.selectedFile) {
      this.notification.error('Please select a file to upload.', 'Error');
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadComplete = false;
    this.isConvertingPdf = false;

    // A PDF is converted to PNG before upload, so what gets stored is an image. Everything
    // downstream — the saved-designs grid, Design Huddle's project media, getDesignDims' placement
    // sizing, the exported print file — consumes the stored URL as an image and none of them can
    // read a PDF. Converting once here beats special-casing PDFs at every one of those points.
    this.resolveUploadPayload().then((payload) => {
      if (!payload) {
        this.isUploading = false;
        this.isConvertingPdf = false;
        this.uploadProgress = 0;
        this.notification.error(
          'This PDF could not be converted to an image. Please upload a PNG, JPG or SVG instead.',
          'Error'
        );
        return;
      }

      this.sendUpload(payload.base64, payload.extension);
    }).catch((err) => {
      // Without this a failed file read would leave the UI stuck showing an upload in progress.
      this.isUploading = false;
      this.isConvertingPdf = false;
      this.uploadProgress = 0;
      this.errorHandler.handleError(err);
    });
  }

  // Produces the bytes to upload: PDFs are rasterized to PNG, everything else is sent as-is.
  // Resolves to null if a PDF could not be rendered.
  private async resolveUploadPayload(): Promise<{ base64: string, extension: string } | null> {
    const extension = this.selectedFile.name.split('.').pop()?.toLowerCase() || '';

    if (extension === 'pdf') {
      this.isConvertingPdf = true;

      // Re-render smaller if the PNG would breach the server's upload cap. A vector logo never
      // will at 300 DPI; a photographic full-page PDF can, and a hard rejection at that point
      // would be a dead end for the user with nothing they could do about it.
      let dataUrl: string | null = null;
      for (const maxEdge of [4000, 2400, 1400]) {
        dataUrl = await this.pdfThumbnail.rasterizePdfFile(this.selectedFile, 300, maxEdge);
        if (!dataUrl || dataUrl.length <= MAX_UPLOAD_BASE64_LENGTH) { break; }
      }

      this.isConvertingPdf = false;

      if (!dataUrl) { return null; }

      // A logo PDF page commonly has margins well beyond the mark itself; stripNearWhiteBackground
      // above already made those transparent, so trim them off the canvas too rather than leaving
      // them baked into the stored artwork's dimensions.
      dataUrl = await this.imageTrim.trimTransparentPadding(dataUrl);

      // Extension must match the bytes — UploadHuddleLogoHandler sniffs the content and rejects
      // a declared type that disagrees with what was actually sent.
      return { base64: dataUrl.split(',')[1], extension: 'png' };
    }

    if (extension === 'svg') {
      // Vector artwork has no pixel/alpha channel to scan, so trimTransparentPadding doesn't
      // apply — the equivalent "blank margin" here is a viewBox/width/height declared larger
      // than the geometry actually drawn inside it. trimSvgPadding measures the real content via
      // getBBox() and rewrites the box to match, instead of rasterizing to a canvas.
      const svgText = await this.selectedFile.text();
      const trimmed = await this.imageTrim.trimSvgPadding(svgText);
      return { base64: this.utf8ToBase64(trimmed), extension };
    }

    let dataUrl = await this.readFileAsDataUrl(this.selectedFile);

    // PNGs are frequently exported on a larger transparent artboard than the visible mark. Nothing
    // downstream (Design Huddle, getDesignDims/topAnchoredMediaPosition) knows to look past that
    // padding, so trim it here — JPG has no alpha channel, so this only does real work for PNG;
    // it's a safe no-op otherwise.
    if (extension === 'png') {
      dataUrl = await this.imageTrim.trimTransparentPadding(dataUrl);
    }

    return { base64: dataUrl.split(',')[1], extension };
  }

  // btoa() only handles Latin1 — this round-trip through encodeURIComponent lets it safely
  // base64-encode arbitrary UTF-8 SVG markup (e.g. a designer's name in a <title> or <desc>).
  private utf8ToBase64(text: string): string {
    return btoa(unescape(encodeURIComponent(text)));
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private sendUpload(base64: string, extension: string): void {
    // Simulate progress while uploading
    const progressInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 10;
      }
    }, 100);

    // Call the API
    this.commonService.uploadHuddleLogo({ File: base64, Extension: extension }).subscribe(
      (response: any) => {
        clearInterval(progressInterval);
        this.uploadProgress = 100;
        this.isUploading = false;
        this.uploadComplete = true;

        if (response.StatusCode === 200) {
          const fileName = response.Data.ResponseData;
          this.uploadedLogoUrl = environment.huddleLogoFolder + fileName;

          const isExisting = response.Message === 'File already exists';
          this.notification.success(
            isExisting ? 'Logo already exists, using existing file.' : 'Logo uploaded successfully!',
            'Success'
          );

          // Store the uploaded file data in state
          this.stateService.setDesignData({
            type: 'uploaded',
            file: this.selectedFile,
            logoUrl: this.uploadedLogoUrl,
            fileName: fileName
          });

          // Navigate to design preview page
          setTimeout(() => {
            this.router.navigate([`${getRoutePrefix(this.router.url)}/design-preview`], {
              queryParams: {
                storeId: this.storeId,
                storeName: this.storeName
              }
            });
          }, 500);
        } else {
          this.notification.error(response.Message || 'Upload failed', 'Error');
          this.uploadComplete = false;
        }
      },
      (error) => {
        clearInterval(progressInterval);
        this.isUploading = false;
        this.uploadProgress = 0;
        this.errorHandler.handleError(error);
      }
    );
  }

  // Saved designs handlers — infinite-scroll pagination

  // Resets state and re-fetches page 1 (called on search change and on tab open).
  loadSavedDesigns(): void {
    this.savedDesigns = [];
    this.currentSkip = 0;
    this.allDesignsLoaded = false;
    this.isLoadingDesigns = false;
    this.isLoadingMore = false;
    this.fetchNextPage();
  }

  // Fetches one PAGE_SIZE page and appends to savedDesigns.
  // Loading guard prevents duplicate in-flight requests from rapid scroll events.
  private fetchNextPage(): void {
    if (this.allDesignsLoaded || this.isLoadingDesigns || this.isLoadingMore) {
      return;
    }

    const isFirstPage = this.currentSkip === 0;
    if (isFirstPage) {
      this.isLoadingDesigns = true;
    } else {
      this.isLoadingMore = true;
    }

    this.uploadDesignService.getUploadedDesigns(this.searchText || '', this.currentSkip, PAGE_SIZE, this.storeId).subscribe(
      (res: any) => {
        if (res.StatusCode === 200 && res.Data?.ResponseData) {
          const page: SavedDesign[] = res.Data.ResponseData.map((d: any) => ({
            id: d.UploadedDesignId,
            name: d.DesignName || 'Untitled Design',
            thumbnailUrl: d.AzureBlobDesignFileUrl || d.UploadedBase64FileUrl || 'assets/img/default-design.png',
            createdDate: d.CreatedDate,
            designId: d.DesignHuddleDesignId || d.InkSoftDesignId || d.UploadedDesignId
          }));

          // Start rasterizing any PDFs in this page. Fire-and-forget: each card binds the result
          // once it lands, and a failure just leaves that card on its file-type tile.
          page
            .filter((d) => this.pdfThumbnail.isPdfUrl(d.thumbnailUrl))
            .forEach((d) => this.pdfThumbnail.render(d.thumbnailUrl, PDF_THUMBNAIL_WIDTH));

          this.savedDesigns = [...this.savedDesigns, ...page];
          this.savedDesignsTotal = res.Data?.TotalDataCount ?? this.savedDesigns.length;
          this.currentSkip += page.length;

          // Stop when the returned page is shorter than a full page or all records are loaded.
          if (page.length < PAGE_SIZE || this.savedDesigns.length >= this.savedDesignsTotal) {
            this.allDesignsLoaded = true;
          }
        } else if (isFirstPage) {
          this.savedDesigns = [];
          this.allDesignsLoaded = true;
        }

        this.isLoadingDesigns = false;
        this.isLoadingMore = false;
      },
      (err) => {
        this.isLoadingDesigns = false;
        this.isLoadingMore = false;
        this.errorHandler.handleError(err);
      }
    );
  }

  // Called by the IntersectionObserver when the sentinel scrolls into the viewport.
  onScroll(): void {
    if (this.isLoadingDesigns || this.isLoadingMore || this.allDesignsLoaded) { return; }
    this.fetchNextPage();
  }

  // Called on (input) from the search box — resets to page 1 for new query.
  onSearchChange(): void {
    this.loadSavedDesigns();
  }

  selectDesign(design: SavedDesign): void {
    this.notification.success(`Selected design: ${design.name}`, 'Success');

    // Store the selected design data in state and navigate to preview
    this.stateService.setDesignData({
      type: 'saved',
      design: design
    });

    this.router.navigate([`${getRoutePrefix(this.router.url)}/design-preview`], {
      queryParams: {
        storeId: this.storeId,
        storeName: this.storeName,
        designId: design.designId
      }
    });
  }
}
